import {
    activate,
    ensureInitialized,
    fetchAndActivate,
    getValue,
    onConfigUpdate,
    RemoteConfig,
} from 'firebase/remote-config'
import * as Sentry from '@sentry/react'
import { getEnvironment } from './utils/environment'

/**
 * Remote Config mechanics:
 *
 * - By default, getValue() only reads whatever value is currently ACTIVE in the local cache.
 *   It never contacts the server on its own.
 *
 * - fetchAndActivate() is a manual pull. It fetches the latest template from Firebase
 *   (subject to minimumFetchIntervalMillis) and then activates it.
 *   Without either a fetch or a real-time update, values remain stale.
 *
 * - Real-time Remote Config (onConfigUpdate) establishes a persistent HTTP connection
 *   to the Remote Config backend while the app is in the foreground.
 *   The client announces the version it currently has. The server keeps the connection open.
 *
 * - When a new template is published on the server, the backend sends an invalidation
 *   message over that open connection. The SDK automatically fetches the new template
 *   (bypassing any minimumFetchIntervalMillis), and then invokes the callback you passed
 *   to onConfigUpdate.
 *
 * - Your callback usually calls activate() to make the new template the active one,
 *   so getValue() will start returning the updated parameters.
 *
 * - Typical pattern:
 *   1) Call fetchAndActivate() once at startup to ensure you get the latest on launch.
 *   2) Register onConfigUpdate() to get real-time pushes during the session.
 *   3) Use getValue() anywhere else in the app to read the currently active values.
 */

const defaultConfig = {
    enable_gemini_dynamic_sliding_window: false,
}

export async function initRemoteConfig(rc: RemoteConfig): Promise<void> {
    rc.defaultConfig = defaultConfig

    // getRemoteConfig() starts warming the cache from IndexedDB without keeping the promise, so its
    // rejection is nobody's until we adopt it here. Must run in the same tick as getRemoteConfig(),
    // which is why firebaseConfig.ts calls us straight after creating the instance.
    ensureInitialized(rc).catch((err) => {
        console.warn('[remoteConfig] Failed to warm the config cache', err)
    })

    if (getEnvironment() !== 'production') {
        console.info('[remoteConfig] Non-prod environment, using default config')
        updateSentryTags(rc)
        return
    }

    try {
        await fetchAndActivate(rc)
        console.info('[remoteConfig] Remote config fetched and activated')
    } catch (err) {
        console.warn('[remoteConfig] Failed to fetch remote config', err)
        await activateCachedConfig(rc)
    }

    updateSentryTags(rc)
    initRealtimeUpdate(rc)
}

/**
 * Activates whatever config the local cache holds, treating a failure as a warning.
 *
 * This is the fallback for a failed fetch, so the values it activates are a nicety, not something
 * the app depends on: every read falls back to defaultConfig. The activation reads IndexedDB, which
 * the browser closes the moment the page is backgrounded or unloaded, and letting that lost race
 * reject would report a teardown as an application error (MD-1430).
 * @param rc The Remote Config instance to activate the cached config on.
 */
async function activateCachedConfig(rc: RemoteConfig): Promise<void> {
    try {
        await activate(rc)
    } catch (err) {
        console.warn('[remoteConfig] Failed to activate the cached config', err)
    }
}

function updateSentryTags(rc: RemoteConfig) {
    Object.keys(defaultConfig).forEach((key) => {
        const val = getValue(rc, key).asString()
        Sentry.setTag(`ff.${key}`, val)
        console.info(`[remoteConfig] Set Sentry tag: ${key}=${val}`)
    })
}

function initRealtimeUpdate(rc: RemoteConfig) {
    onConfigUpdate(rc, {
        next: () => {
            activate(rc)
                .then(() => {
                    console.info('[remoteConfig] New config activated via realtime update')
                    updateSentryTags(rc)
                })
                .catch((err) => {
                    console.warn('[remoteConfig] Failed to activate updated config', err)
                })
        },
        error: (error: Error) => {
            console.warn('[remoteConfig] Config update error:', error)
        },
        complete: () => {
            console.log('[remoteConfig] Config update listener completed')
        },
    })
}
